import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Lock, Download, RefreshCw, AlertTriangle, ArrowDown } from "lucide-react";
import { getPoolWizard, getPoolStep } from "@/api";

const HOW = ["Open your Safe → Apps → Transaction Builder.", "Drag this file into the page.", "Check it shows exactly 1 transaction.", "Click Create Batch → Send Batch → Sign / Execute."];

function StepCard({ step, status, onDownload, busy, warn, cardRef }) {
  const done = status === "done";
  const locked = status === "locked";
  return (
    <div ref={cardRef} data-testid={`wizard-step-${step.n}`} data-status={status}
      className={`card-tile flex gap-4 transition-opacity ${locked ? "opacity-50" : ""} ${status === "active" ? "card-flame" : ""}`}>
      <div className={`shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-display text-2xl font-black ${done ? "bg-emerald-500/20 text-emerald-300" : status === "active" ? "bg-orange-500/20 text-orange-300" : "bg-white/5 text-slate-500"}`}>
        {done ? <CheckCircle2 size={26} /> : locked ? <Lock size={20} /> : step.n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-lg font-bold">{step.title}</div>
        <p className="text-sm text-slate-400 mt-1">{step.summary}</p>
        <div className="text-xs text-slate-500 mt-1 break-all">Talks to: <span className="font-mono">{step.to}</span></div>
        {done && <div className="mt-2 text-sm text-emerald-300" data-testid={`wizard-step-${step.n}-done`}>Confirmed on-chain.</div>}
        {warn && <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200 flex gap-2" data-testid={`wizard-step-${step.n}-warning`}><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {warn}</div>}
        {status === "active" && !warn && (
          <>
            <button data-testid={`wizard-step-${step.n}-download`} onClick={() => onDownload(step.n)} disabled={busy} className="btn-tactile btn-flame text-sm mt-3 flex items-center gap-1.5">
              <Download size={14} /> Download step {step.n} file
            </button>
            <ol className="mt-3 text-xs text-slate-400 list-decimal pl-5 space-y-0.5" data-testid={`wizard-step-${step.n}-how`}>
              {HOW.map((h) => <li key={h}>{h}</li>)}
              <li>Come back here. This step turns green by itself once it has executed.</li>
            </ol>
          </>
        )}
        {locked && <div className="mt-2 text-xs text-slate-500">Unlocks when the previous step is confirmed on-chain.</div>}
      </div>
    </div>
  );
}

export const PoolWizard = ({ token, showToast, onChange }) => {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);
  const refs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const prevActive = useRef(null);

  const load = useCallback(() => getPoolWizard(token).then((d) => { setW(d); onChange?.(); }).catch(() => {}), [token]); // eslint-disable-line
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const st = w?.state;
  const doneFlags = st ? [st.step1_done, st.step2_done, st.step3_done, st.step4_done] : [];
  const active = doneFlags.findIndex((d) => !d);
  useEffect(() => {
    if (prevActive.current !== null && active > prevActive.current && active >= 0) {
      showToast(`Step ${active} confirmed. Next: step ${active + 1}`);
      refs[active].current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (active !== -1 || prevActive.current !== null) prevActive.current = active;
  }, [active]); // eslint-disable-line

  if (!w) return <div className="text-slate-400">Reading the blockchain…</div>;

  const download = async (n) => {
    setBusy(true);
    try {
      const { bundle, filename } = await getPoolStep(token, n);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" }));
      a.download = filename; a.click();
      showToast(`Step ${n} file downloaded. Import it in Safe → Transaction Builder`);
    } catch (e) { showToast(e?.response?.data?.detail || "Download failed", "error"); }
    finally { setBusy(false); }
  };

  const priceWarn = st.step2_done && !st.step3_done && !st.price_ok
    ? `The pool exists but its price (${st.price_cro ? st.price_cro.toPrecision(4) : "unknown"} CRO per FLAME) doesn't match the planned ${st.planned_price_cro} CRO. Don't add liquidity. Contact support before continuing.`
    : null;
  const lowGas = st.cro_gas_balance < 3;
  const lockWarn = st.step3_done && !st.step4_done && !(st.lock?.deployed && st.lock?.valid)
    ? "The FlameLPLock contract isn't live yet. Your developer deploys and verifies it first; this step unlocks automatically after that."
    : null;
  const warnFor = (i) => (i !== active ? null : i === 2 ? priceWarn : i === 3 ? lockWarn : null);

  return (
    <div data-testid="pool-wizard">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-400">Safe gas balance:</span>
        <span className={`font-mono ${lowGas ? "text-amber-300" : "text-emerald-300"}`} data-testid="pool-gas-balance">{st.cro_gas_balance.toFixed(4)} CRO</span>
        {lowGas && <span className="text-amber-300" data-testid="pool-gas-warning">· send about 5 CRO to the Safe before signing</span>}
        <div className="flex-1" />
        <button onClick={load} className="btn-tactile btn-ghost text-xs flex items-center gap-1" data-testid="pool-refresh"><RefreshCw size={12} /> Check again</button>
      </div>
      {active === -1 && <div className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-200" data-testid="wizard-complete">The pool is live and the liquidity is locked{st.lock?.unlock_time ? ` until ${new Date(st.lock.unlock_time * 1000).toISOString().slice(0, 10)}` : ""}. Next: add your social links and publish a white paper update.</div>}
      <div className="mt-4 space-y-3">
        {w.steps.map((s, i) => (
          <div key={s.n}>
            <StepCard step={s} cardRef={refs[i]} busy={busy} onDownload={download}
              status={doneFlags[i] ? "done" : i === active ? "active" : "locked"} warn={warnFor(i)} />
            {i < w.steps.length - 1 && <div className="flex justify-center py-1 text-slate-600"><ArrowDown size={16} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};
